/* eslint-disable react-hooks/exhaustive-deps */
import { DomainTypeEnum } from '@pypestream/api-services';
import {
  Button,
  Modal,
  ModalIcon,
  ModalProps,
  Stack,
  TextBody,
  TextTitle,
  Spacer,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';
import { FC, useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  Environments,
  FormProjectStateType,
  Loader,
  ProjectEnvironmentsType,
  ReturnFormType,
} from '../../../../components';
import { OrganizationRoleNames, useAuthRole } from '../../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
} from '../../../../xstate/app.xstate';
import { ProjectDetailsTabs } from '../project-details';
import {
  ProjectSettingsEnvironmentsTab,
  ProjectSettingsGeneralTab,
} from './index';
import { GetProjectDetailsQuery } from '@pypestream/api-services/urql';

export const sortEnvironmentsByName = (
  arr: {
    domains: {
      type: DomainTypeEnum | undefined;
      url: string | undefined;
    }[];
    description: string | undefined;
    name: string;
  }[]
) => {
  const namesOrder: {
    [key: string]: number;
  } = {
    [Environments.Production]: 3,
    [Environments.Testing]: 2,
    [Environments.Development]: 1,
  };
  return arr.sort((a, b) => namesOrder[a.name] - namesOrder[b.name]);
};

export const RenderProjectDetailsContent: FC<{
  project?: NonNullable<GetProjectDetailsQuery['admin_']>['projectById'];
  tab?: ProjectDetailsTabs;
  environmentId?: string;
  hideDeleteButton?: boolean;
}> = ({ project, tab, environmentId, hideDeleteButton = false }) => {
  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const [showDeleteTeamConfirmationModal, setShowDeleteTeamConfirmationModal] =
    useState(false);
  const isDeleting = useManagerStateMatches(
    'orgRelated.ready.projectDetails.deleting'
  );
  const isProjectDeleted = useManagerStateMatches(
    'orgRelated.ready.projectDetails.projectDeleted'
  );

  const navigate = useNavigate();

  const form = useForm<FormProjectStateType>({
    defaultValues: {
      name: '',
      description: '',
      projectIconId: undefined,
      projectIcon: '',
      timeZoneId: '',
      productIds: [],
      applets: [],
      projectEnvironmentConfigs: [],
    },
  });

  const { reset, handleSubmit } = form;

  const { products, accountId, routes, applets, tools } = useManagerCtxSelector(
    (ctx) => ({
      products: ctx.products,
      tools: ctx.tools,
      applets: ctx.applets,
      accountId: ctx.selectedOrgId,
      routes: ctx.routes,
    })
  );
  const productTools = useMemo(() => {
    return tools?.filter(({ productId }) => {
      return products?.find(({ id }) => id === productId);
    });
  }, [tools, products]);

  const projectId = useMemo(() => project?.projectId, [project]);

  const sortedProjectEnvironments = sortEnvironmentsByName(
    (project?.projectEnvironmentConfig || []).map(
      ({ domains, appletInstances, environment, description, id }) => ({
        domains:
          domains?.map(({ type, url }) => ({
            type,
            url,
          })) || [],
        description,
        name: environment?.name || '',
        appletInstances: appletInstances,
        id,
      })
    )
  ) as ProjectEnvironmentsType;

  const navigateToProjects = useCallback(
    () => navigate(routes.projects),
    [navigate, routes.projects]
  );

  const onSubmit = useCallback(
    ({ projectEnvironmentConfigs, ...restData }: FormProjectStateType) => {
      sendManagerEvent({
        type: 'manager.updateProject',
        projectId,
        ...restData,
        environmentConfig: projectEnvironmentConfigs.map((config) => ({
          domains: config.domains || [],
          description: config.description || '',
          name: config.name,
        })),
      });
    },
    [navigate, projectId, routes.projects]
  );

  const renderGeneralTab = useMemo(
    () => (
      <ProjectSettingsGeneralTab
        form={form as unknown as ReturnFormType}
        projectId={projectId}
        products={productTools}
        accountId={accountId}
        disabled={!isAdminRole}
      />
    ),
    [form, projectId, productTools, accountId, isAdminRole]
  );

  const renderEnvironmentsTab = useMemo(
    () => (
      <ProjectSettingsEnvironmentsTab
        form={form as unknown as ReturnFormType}
        accountId={accountId}
        projectId={projectId!}
        projectEnvironments={sortedProjectEnvironments}
        disabled={!isAdminRole}
        environmentId={environmentId}
      />
    ),
    [
      form,
      accountId,
      projectId,
      sortedProjectEnvironments,
      isAdminRole,
      environmentId,
    ]
  );

  const getLayout = useCallback(
    (tabName?: ProjectDetailsTabs) => {
      switch (tabName) {
        case ProjectDetailsTabs.general:
          return renderGeneralTab;
        case ProjectDetailsTabs.environments:
          return renderEnvironmentsTab;
        default:
          return renderGeneralTab;
      }
    },
    [renderGeneralTab, renderEnvironmentsTab]
  );

  useEffect(() => {
    if (!project) return;

    const projectDetails = {
      name: project.name,
      description: project.description,
      projectIconId: undefined,
      projectIcon: project.pictureFile?.url || '',
      timeZoneId: project.localizationSettings?.timeZone?.id,
      productIds: project.projectProductSettings?.map(
        ({ productId }) => productId
      ),
      applets: applets,
      projectEnvironmentConfigs: sortedProjectEnvironments,
    };

    form.reset({ ...projectDetails });
  }, [project, applets]);

  useEffect(() => {
    if (isProjectDeleted) {
      navigateToProjects();
    }
  }, [isProjectDeleted]);

  return !project ? (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Loader relative />
    </div>
  ) : (
    <form>
      {getLayout(tab)}
      <Stack
        justifyContent={hideDeleteButton ? 'end' : 'space-between'}
        gutter="small"
      >
        <Modal
          test-id="delete-project-modal"
          size="medium"
          open={showDeleteTeamConfirmationModal || isDeleting}
          onClose={() => setShowDeleteTeamConfirmationModal(false)}
        >
          <ModalIcon
            name="error"
            slot="header"
            style={{ display: 'flex', justifyContent: 'center' }}
          />
          <Stack slot="header" alignItems="center" direction="column">
            <TextTitle size="small">
              <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.areYouSure">
                Are you sure?
              </TranslationComponent>
            </TextTitle>
            <TextBody variant="secondary">
              <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.youWillLose">
                You&lsquo;ll permanently lose:
              </TranslationComponent>
              <ul>
                <li>
                  <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.accessToProjectTools">
                    access to this Project&lsquo;s tool instances
                  </TranslationComponent>
                </li>
                <li>
                  <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.dataInsideProjectTools">
                    all data inside this Project&lsquo;s tool instances
                  </TranslationComponent>
                </li>
                <li>
                  <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.rolesAssignedForProjectTools">
                    all roles assigned for this Project&lsquo;s tool instances
                  </TranslationComponent>
                </li>
              </ul>
            </TextBody>
          </Stack>
          <Stack slot="footer" justifyContent="end">
            <Button
              test-id="delete-project-cancel"
              variant="ghost"
              size="large"
              onClick={() => setShowDeleteTeamConfirmationModal(false)}
              disabled={isDeleting}
            >
              <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.cancel">
                Cancel
              </TranslationComponent>
            </Button>
            <Button
              test-id="delete-project-confirm"
              size="large"
              variant="warning"
              data-cy="delete-project"
              onClick={(e) => {
                sendManagerEvent({
                  type: 'manager.deleteProject',
                  projectId,
                });

                setShowDeleteTeamConfirmationModal(false);
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.removingProject">
                  Deleting Project...
                </TranslationComponent>
              ) : (
                <TranslationComponent i18nKey="manager/projects:projectDetails.deleteModal.removeProject">
                  Delete Project
                </TranslationComponent>
              )}
            </Button>
          </Stack>
        </Modal>
        {isAdminRole && !hideDeleteButton && (
          <Stack>
            <Button
              test-id="project-details-delete-btn"
              variant="warning"
              size="large"
              onClick={() => setShowDeleteTeamConfirmationModal(true)}
            >
              <TranslationComponent i18nKey="manager/projects:projectDetails.delete">
                Delete Project
              </TranslationComponent>
            </Button>
          </Stack>
        )}

        {isAdminRole && (
          <Stack>
            <Button
              test-id="project-details-cancel-btn"
              variant="secondary"
              size="large"
              onClick={() => {
                reset();
                navigateToProjects();
              }}
            >
              <TranslationComponent i18nKey="manager/projects:projectDetails.cancel">
                Cancel
              </TranslationComponent>
            </Button>
            <Button
              test-id="project-details-save-btn"
              size="large"
              onClick={handleSubmit(onSubmit)}
              disabled={!Object.keys(form.formState.dirtyFields).length}
            >
              <TranslationComponent i18nKey="manager/projects:projectDetails.save">
                Save
              </TranslationComponent>
            </Button>
          </Stack>
        )}
      </Stack>
    </form>
  );
};
